export const WARRANTY_CYCLE_THRESHOLD = 150_000;

export type WarrantyStatus =
  | {kind: 'no-date'}
  | {kind: 'expired-date'; date: string; expiredSince: string}
  | {kind: 'expired-cycles'}
  | {kind: 'valid'; date: string};

/** Parse une chaîne JJ/MM/AAAA en Date (minuit local). Retourne undefined si invalide. */
export function parseDdMmYyyy(value?: string): Date | undefined {
  if (value === undefined) return undefined;
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return undefined;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // Rejet des dates hors-plage (ex: 32/13/2025) : Date normalise silencieusement.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined;
  }
  return date;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Formate une Date en chaîne JJ/MM/AAAA. */
export function formatDdMmYyyy(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Calcule le statut de garantie d'une porte.
 *
 * Ordre d'évaluation :
 *   1. Date absente / illisible          -> no-date
 *   2. Date passée                       -> expired-date
 *   3. Date valide + cycles > seuil      -> expired-cycles
 *   4. Date valide + cycles <= seuil     -> valid
 */
export function computeWarrantyStatus(
  warrantyEndDate: string | undefined,
  doorCycles: number | undefined,
  now: Date = new Date()
): WarrantyStatus {
  const endDate = parseDdMmYyyy(warrantyEndDate);
  if (endDate === undefined) {
    return {kind: 'no-date'};
  }

  const today = startOfDay(now);
  if (endDate.getTime() < today.getTime()) {
    const nextDay = new Date(endDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return {
      kind: 'expired-date',
      date: warrantyEndDate as string,
      expiredSince: formatDdMmYyyy(nextDay),
    };
  }

  if (doorCycles !== undefined && doorCycles > WARRANTY_CYCLE_THRESHOLD) {
    return {kind: 'expired-cycles'};
  }

  return {kind: 'valid', date: warrantyEndDate as string};
}
